import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum RecommendationKind {
  DISCOVER_PRODUCTS = "discover_products",
  SIMILAR_PRODUCTS = "similar_products",
  COMPLEMENTARY_PRODUCTS = "complementary_products",
  POPULAR_PRODUCTS = "popular_products",
  EXCHANGE_PRODUCTS = "exchange_products",
}

export type FallbackKind = RecommendationKind | "NONE";

export enum TimeWindow {
  SEVEN_DAYS = "7d",
  THIRTY_DAYS = "30d",
  NINETY_DAYS = "90d",
}

export const baseRecommendationShopperInputSchema = z.object({
  customerId: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
});
export type RecommendationShopperInput = z.infer<
  typeof baseRecommendationShopperInputSchema
>;

const baseRecommendationFilterInputSchema = z.object({
  /**
   * Used for filtering out low-quality products in similar, complementary, and discover recommendations.
   */
  qualityThreshold: z.number().min(0).max(1).optional(),

  /**
   * Used for popular products recommendations.
   */
  timeWindow: createEnum(TimeWindow).optional(),

  /** Matched against Product2.collections; non-members are dropped, not replaced. */
  collections: z.array(z.string()).optional(),
});

const baseRecommendationInputSchema = z.object({
  teamId: z.string(),
  shopper: baseRecommendationShopperInputSchema,
  limit: z.number().optional(),
  productId: z.string().optional(),
  filters: baseRecommendationFilterInputSchema.optional(),
});

// After getting the customerId, we can omit the shopper object for a cleaner input schema
export const baseRecommendationInputSchemaWithResolvedCustomerId =
  baseRecommendationInputSchema
    .extend({ customerId: z.union([z.string(), z.null()]) })
    .omit({ shopper: true });

export type BaseRecommendationInputWithCustomerId = z.infer<
  typeof baseRecommendationInputSchemaWithResolvedCustomerId
>;

export const fallbackKindSchema = z.union([
  createEnum(RecommendationKind),
  z.literal("NONE"),
]);

export const recommendationKindSchema = createEnum(RecommendationKind);

export const getProductRecommendationsInputSchema =
  baseRecommendationInputSchema.extend({
    kind: recommendationKindSchema,
    fallbackKind: fallbackKindSchema.optional(),
  });

export type GetProductRecommendationsInput = z.infer<
  typeof getProductRecommendationsInputSchema
>;

export function getProductRecommendationsCacheKey(
  input: GetProductRecommendationsInput,
): string {
  return `${input.teamId}-${input.productId}-${input.kind}-${input.fallbackKind}-${input.shopper.customerId}-${input.shopper.email}-${input.shopper.phone}-${input.limit}-${input.filters?.qualityThreshold}-${input.filters?.timeWindow?.toString()}-${input.filters?.collections?.join(",")}`;
}

// Tool-specific parameters schema for customer agents
export const productRecommendationsToolParametersSchema = z.object({
  kind: recommendationKindSchema.describe(
    "Type of recommendations: discover_products (based on customer preferences), similar_products (based on a specific product), or complementary_products (cross-sell recommendations)",
  ),
  fallbackKind: fallbackKindSchema
    .optional()
    .default(RecommendationKind.SIMILAR_PRODUCTS)
    .describe(
      "Fallback recommendation type to use if primary returns no results. Defaults to similar_products.",
    ),
  productId: z
    .string()
    .optional()
    .describe(
      "Product ID for similar or complementary product recommendations",
    ),
  limit: z
    .number()
    .optional()
    .describe("Maximum number of recommendations to return"),
  filters: baseRecommendationFilterInputSchema.optional(),
});

export type ProductRecommendationsToolParameters = z.infer<
  typeof productRecommendationsToolParametersSchema
>;
